import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import Select from 'react-select';
import { generateWhatsAppLink, generateWhatsAppMessageLink } from './utils/whatsapp';

// Componente de efeito de digitação otimizado com React.memo
const TypingEffect = React.memo(({ words, typingSpeed = 100, deletingSpeed = 50, pauseTime = 2000 }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[currentWordIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Digitando
        if (currentText.length < currentWord.length) {
          setCurrentText(currentWord.substring(0, currentText.length + 1));
        } else {
          // Palavra completa, aguardar antes de deletar
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        // Deletando
        if (currentText.length > 0) {
          setCurrentText(currentWord.substring(0, currentText.length - 1));
        } else {
          // Palavra deletada, ir para próxima
          setIsDeleting(false);
          setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex, words, typingSpeed, deletingSpeed, pauseTime]);

  return (
    <span style={{ color: 'white', fontWeight: '800' }}>
      {currentText}
      <span className="typing-cursor">|</span>
    </span>
  );
});

// Componente para animação de números otimizado com React.memo
const AnimatedNumber = React.memo(({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [inView, end, duration]);

  return <span ref={ref}>{count}</span>;
});

// Componente para seções com animação fade
const FadeInSection = ({ children, className = '' }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <div ref={ref} className={`fade-in ${inView ? 'visible' : ''} ${className}`}>
      {children}
    </div>
  );
};

// Componente FAQ Item
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="faq-item mb-3" style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <div
        className="faq-question p-4 d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer', borderBottom: isOpen ? '1px solid #eee' : 'none' }}
        onClick={onToggle}
      >
        <h5 className="mb-0" style={{ color: '#000', fontWeight: '600' }}>{question}</h5>
        <span
          className="faq-icon"
          style={{
            fontSize: '20px',
            color: 'var(--primary-green)',
            fontWeight: 'bold',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          +
        </span>
      </div>
      <div
        className="faq-answer-container"
        style={{
          maxHeight: isOpen ? '300px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
          opacity: isOpen ? 1 : 0
        }}
      >
        <div className="faq-answer p-4 pt-0" style={{ color: '#000', lineHeight: '1.6' }}>
          <p className="mb-0">{answer}</p>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentCarouselSlide] = useState(0);
  const [activeTab, setActiveTab] = useState('missao');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openFAQIndex, setOpenFAQIndex] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const floatingWhatsAppEnabled = false;
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Rolar para a âncora quando houver hash na URL (ex.: /#produtos)
  useEffect(() => {
    const scrollToHash = () => {
      if (window.location.hash) {
        const id = decodeURIComponent(window.location.hash.substring(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    const t = setTimeout(scrollToHash, 0);
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      clearTimeout(t);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  // Banner de instrução para iOS (instalar como App)
  useEffect(() => {
    const ua = window.navigator.userAgent;
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIos && !isStandalone && isSafari) {
      setShowInstallBanner(true);
    }
  }, []);

  // Estados para o formulário de contato
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });
  const [formErrors, setFormErrors] = useState({
    nome: false,
    email: false,
    telefone: false
  });

  const normalizePhone = (value) => value.replace(/\D/g, '').slice(0, 11);
  const formatPhone = (value) => {
    const digits = normalizePhone(value);
    const ddd = digits.slice(0, 2);
    const rest = digits.slice(2);
    if (digits.length <= 2) return ddd ? `(${ddd}` : '';
    if (digits.length <= 6) return `(${ddd}) ${rest}`;
    if (digits.length <= 10) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const getRequiredValidation = (data = formData) => {
    const nomeOk = data.nome.trim().length > 0;
    const emailValue = data.email.trim();
    const emailOk = emailValue.length > 0 && isValidEmail(emailValue);
    const phoneDigits = normalizePhone(data.telefone);
    const telefoneOk = phoneDigits.length >= 10;
    return {
      isValid: nomeOk && emailOk && telefoneOk,
      errors: {
        nome: !nomeOk,
        email: !emailOk,
        telefone: !telefoneOk
      }
    };
  };

  const validateRequiredFields = (data = formData) => {
    const { isValid, errors } = getRequiredValidation(data);
    setFormErrors(errors);
    return isValid;
  };

  // Função para atualizar os dados do formulário
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      setFormErrors(prevErrors => {
        if (!prevErrors[field]) return prevErrors;
        if (field === 'nome') {
          return { ...prevErrors, nome: next.nome.trim().length === 0 };
        }
        if (field === 'email') {
          const emailValue = next.email.trim();
          return { ...prevErrors, email: emailValue.length === 0 || !isValidEmail(emailValue) };
        }
        if (field === 'telefone') {
          return { ...prevErrors, telefone: normalizePhone(next.telefone).length < 10 };
        }
        return prevErrors;
      });
      return next;
    });
  };

  // Função para gerar mensagem do WhatsApp com dados do formulário
  const generateWhatsAppMessageFromForm = () => {
    const { nome, email, telefone, assunto, mensagem } = formData;

    let message = 'Olá! Estou entrando em contato através do site da Sustenta LED.\n\n';

    if (nome) message += `📝 Nome: ${nome}\n`;
    if (email) message += `📧 E-mail: ${email}\n`;
    if (telefone) message += `📞 Telefone: ${telefone}\n`;
    if (assunto) {
      const assuntos = {
        'orcamento': 'Solicitar Orçamento',
        'projeto': 'Projeto de Iluminação',
        'produtos': 'Informações sobre Produtos',
        'outros': 'Outros'
      };
      message += `🏷️ Assunto: ${assuntos[assunto] || assunto}\n`;
    }
    if (mensagem) message += `💬 Mensagem: ${mensagem}\n`;

    message += '\nAguardo retorno. Obrigado!';

    return message;
  };

  const isFormValid = getRequiredValidation(formData).isValid;

  const handleWhatsAppClick = (event) => {
    if (!isFormValid) {
      validateRequiredFields();
      event.preventDefault();
      event.stopPropagation();
    }
  };



  // Função para fechar popup
  const closePopup = () => {
    setShowPopup(false);
    document.body.style.overflow = 'auto';
  };

  // Dados dos produtos (comentado - não utilizado atualmente)
  /*
  const productData = {
    lampadas: {
      title: 'Lâmpadas LED',
      description: 'Linha completa de lâmpadas LED com alta eficiência energética e longa durabilidade.',
      images: ['b_lampadas.jpg'],
      types: ['LED Bulbo', 'LED Tubular', 'LED Dicróica', 'LED Filamento']
    },
    luminarias: {
      title: 'Luminárias',
      description: 'Luminárias modernas e funcionais para todos os ambientes.',
      images: ['b_luminarias.jpg'],
      types: ['Pendentes', 'Plafons', 'Arandelas', 'Trilhos']
    },
    refletores: {
      title: 'Refletores',
      description: 'Refletores LED para iluminação externa e de destaque.',
      images: ['b_refletores.jpg'],
      types: ['Refletor Slim', 'Refletor com Sensor', 'Refletor RGB', 'Refletor Solar']
    },
    spots: {
      title: 'Spots',
      description: 'Spots direcionais para iluminação de destaque e decorativa.',
      images: ['b_spots.jpg'],
      types: ['Spot Embutir', 'Spot Sobrepor', 'Spot Trilho', 'Spot Ajustável']
    },
    fitas: {
      title: 'Fitas LED',
      description: 'Fitas LED flexíveis para decoração e iluminação indireta.',
      images: ['b_fitas.jpg'],
      types: ['Fita Monocor', 'Fita RGB', 'Fita Endereçável', 'Fita Neon']
    },
    decoracao: {
      title: 'Decoração',
      description: 'Produtos decorativos com LED para ambientação especial.',
      images: ['b_decoracao.jpg'],
      types: ['Cordão de LED', 'Pisca-Pisca', 'Cortina de LED', 'Árvores Luminosas']
    }
  };
  */

  // Função para abrir modal de produto (comentada - não utilizada atualmente)
  // const openProductModal = (product) => {
  //   setSelectedProduct(productData[product.category]);
  //   setCurrentCarouselSlide(0);
  //   setShowModal(true);
  //   document.body.style.overflow = 'hidden';
  // };

  const closeProductModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    document.body.style.overflow = 'auto';
  };

  // Controle do scroll para o menu e botão scroll to top
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeProductModal();
        closePopup();
      }
    };

    if (showModal || showPopup) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal, showPopup]);

  // Função para trocar de aba
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Função para fechar o navbar mobile
  const closeNavbar = () => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
      const bsCollapse = new window.bootstrap.Collapse(navbarCollapse, {
        toggle: false
      });
      bsCollapse.hide();
    }
  };

  // Função para controlar o carrossel de clientes
  const slideCarousel = (direction) => {
    const totalSlides = 4; // Total de slides únicos
    let newSlide = currentSlide;

    if (direction === 'next') {
      newSlide = (currentSlide + 1) % totalSlides;
    } else {
      newSlide = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
    }

    setCurrentSlide(newSlide);

    const track = document.getElementById('clients-track');
    if (track) {
      const slideWidth = 100 / totalSlides; // Cada slide ocupa 25% da largura
      track.style.transform = `translateX(-${newSlide * slideWidth}%)`;
    }
  };

  // Conteúdo das abas
  const tabContent = {
    missao: {
      content: 'Providenciar soluções de iluminação LED de alta eficiência, aliando tecnologia, sustentabilidade e segurança, para melhorar a vida das pessoas e contribuir para cidades mais iluminadas, seguras e com menor impacto ambiental.'
    },
    visao: {
      content: 'Ser reconhecida como a referência nacional em iluminação pública e privada com LED sustentável, impulsionando cidades inteligentes e ecologicamente responsáveis até 2030, através de inovação contínua, qualidade inigualável e compromisso socioambiental.'
    },
    valores: {
      content: 'Sustentabilidade, Inovação, Qualidade, Transparência, Foco no Cliente e Responsabilidade Social.'
    }
  };

  return (
    <div className="App">
      <Helmet>
        <title>Sustenta LED - A maior distribuidora de LED do Brasil</title>
        <meta name="description" content="Desde 2012 somos um dos principais e-commerce de LED do país com mais de 300 mil vendas realizadas. Encontre produtos de iluminação LED de qualidade." />
      </Helmet>

      {/* Menu Superior */}
      <nav className={`navbar navbar-expand-lg navbar-custom ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <a className="navbar-brand" href="#home">
            <img
              src={scrolled ? "../img/logo_sustenta_preto.png" : "../img/logo_sustenta_branco.png"}
              alt="Sustenta LED - Distribuidora de LED, Importadora de Lâmpadas LED e Atacadista de Luminárias LED"
            />
          </a>

          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <a className="nav-link" href="#home" onClick={closeNavbar}>Home</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#sobre-a-sustentaled" onClick={closeNavbar}>Sobre Nós</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#comotrabalhamos" onClick={closeNavbar}>Como Trabalhamos</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#produtos" onClick={closeNavbar}>Produtos</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#cases" onClick={closeNavbar}>Cases</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#faq" onClick={closeNavbar}>FAQ</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#contact" onClick={closeNavbar}>Contato</a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Banner Hero */}
      <section id="home" className="hero-section">
        <div className="container">
          <FadeInSection>
            <div className="hero-content">
              <h1>
                Está em busca de<br />
                <TypingEffect
                  words={[
                    'refletores de LED?',
                    'luminárias públicas?',
                    'fitas de LED?',
                    'linha solar?',
                    'lâmpadas de LED?',
                    'luminárias high bay ou ufo?',
                    'perfis de LED?'
                  ]}
                  typingSpeed={80}
                  deletingSpeed={40}
                  pauseTime={2500}
                />
              </h1>
              <p style={{ paddingTop: '0px' }}>
                Na Sustenta LED você encontra estes<br />
                e muitos outros produtos de iluminação LED
              </p>
              <div className="hero-buttons" style={{ paddingTop: '30px' }}>
                <a href="#contact" className="btn-primary-green">Cotar agora!</a>
                <a href="#sobre-a-sustentaled" className="btn-outline-white">Saber mais</a>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Sobre Nós */}
      <section id="sobre-a-sustentaled" className="section">
        <div className="container">
          <FadeInSection>
            <div className="section-title">
              <div className="badge-green">Sobre Nós</div>
              <h1>A maior distribuidora de LED do Brasil</h1>
              <h4>Desde 2012 somos um dos principais e-commerce de LED do país com mais de 300 mil vendas realizadas</h4>
            </div>
          </FadeInSection>

          <div className="row align-items-center">
            <div className="col-lg-6">
              <FadeInSection>
                <img
                  src="../img/banner_quemsomos_sustentaled.jpg"
                  alt="Sustenta LED - Distribuidora de LED líder no Brasil desde 2012, importadora de lâmpadas LED e atacadista de luminárias LED"
                  className="img-fluid rounded"
                />
              </FadeInSection>
            </div>
            <div className="col-lg-6">
              <FadeInSection>
                <p>
                  Somos uma familiar criada em Joinville, Santa Catarina em 2013, visionando mais que um atendimento, uma relação de confiança, amizade e muito respeito. Pensamos nas melhores soluções de iluminação para sua casa, loja, condomínio, empresa e estabelecimento. Somos importadores e possuímos a maior exclusividade e tecnologia em lâmpadas LED, a loja online oferece qualidade, durabilidade, sofisticação, conforto e praticidade para você e sua família . Com uma gama completa de produtos, você encontra todos os componentes de iluminação para sua empresa e sua casa.
                </p>
                <p>
                  A nossa loja é especializada em refletores, luminárias, plafons, spot's, fitas de LED, lâmpadas tubulares, iluminação pública, industrial, automotiva, iluminação solar, CFTV e acessórios. Na SUSTENTALED, você encontra diversos modelos e as mais variadas potências de tecnologia LED compatíveis para atender perfeitamente o seu projeto e necessidade.
                </p>

                <div className="mission-tabs mt-4">
                  <div className="tab-buttons">
                    <button
                      className={`tab-button ${activeTab === 'missao' ? 'active' : ''}`}
                      onClick={() => handleTabChange('missao')}
                    >
                      Missão
                    </button>
                    <button
                      className={`tab-button ${activeTab === 'visao' ? 'active' : ''}`}
                      onClick={() => handleTabChange('visao')}
                    >
                      Visão
                    </button>
                    <button
                      className={`tab-button ${activeTab === 'valores' ? 'active' : ''}`}
                      onClick={() => handleTabChange('valores')}
                    >
                      Valores
                    </button>
                  </div>
                  <div className="tab-content">
                    <h5 style={{ color: 'var(--primary-green)', fontWeight: '600' }}>
                      {tabContent[activeTab].title}
                    </h5>
                    <p>{tabContent[activeTab].content}</p>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* Como Trabalhamos */}
      <section id="comotrabalhamos" className="section" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <FadeInSection>
            <div className="section-title">
              <div className="badge-green">Como Trabalhamos</div>
              <h1>Nossa metodologia de atendimento</h1>
              <h4>Processo otimizado para melhor atender nossos clientes</h4>
            </div>
          </FadeInSection>

          <div className="row align-items-center mb-5">
            <div className="col-lg-6">
              <FadeInSection>
                <p>
                  Na Sustenta LED, cada cliente é acompanhado por <b>consultores especializados,</b> preparados para entender suas necessidades e indicar as soluções mais adequadas em iluminação. Nosso atendimento vai além da venda: buscamos construir uma relação de confiança, oferecendo orientações técnicas e personalizadas para cada tipo de projeto.
                </p>
                <p>
                  Trabalhamos com um <b>processo de consultoria prática e ágil,</b> no qual avaliamos o ambiente, o objetivo da iluminação e o melhor custo-benefício, garantindo qualidade, eficiência e sofisticação. Dessa forma, cada produto recomendado é pensado para unir durabilidade e economia de energia.
                </p>
                <p>
                  Nosso compromisso é entregar <b>experiência completa e transparente,</b> desde o primeiro contato até o pós-venda. Acompanhamos cada etapa do processo para assegurar que você tenha a solução certa, no tempo certo, com todo o suporte necessário para o sucesso do seu projeto.
                </p>
                <div className="text-center mt-4">
                  <a href="#eusou" className="btn btn-success btn-lg px-4 py-3" style={{ backgroundColor: 'var(--primary-green)', borderColor: 'var(--primary-green)', fontWeight: '400', fontSize: '1.1rem' }}>
                    Receba uma cotação em até 1h
                  </a>
                </div>
              </FadeInSection>
            </div>
            <div className="col-lg-6">
              <FadeInSection>
                <img
                  src="../img/banner_como_trabalhamos.jpg"
                  alt="Como Trabalhamos"
                  className="img-fluid rounded"
                />
              </FadeInSection>
            </div>
          </div>

          <FadeInSection>
            <div id="eusou" style={{ scrollMarginTop: '120px' }}>
              <div className="section-title">
                <h1>Atendemos todos os setores</h1>
                <h4>Receba uma cotação com valores exclusivos para o seu segmento</h4>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection>
            <div className="row">
              {/* Primeira Coluna */}
              <div className="col-lg-6">
                {/* Banner Indústria 100% */}
                <div className="row mb-3">
                  <div className="col-12">
                    <Link
                      to="/led-para-industria"
                      className="client-banner industria-banner d-block"
                      style={{ height: '630px' }}
                    >
                      <img src="../img/clientes/banner_industria.jpg" alt="Indústria - Distribuidora de LED para setor industrial, importadora de lâmpadas LED industriais" style={{ height: '630px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Indústria</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Segunda Coluna */}
              <div className="col-lg-6">
                {/* Primeira Linha - Comércio 100% */}
                <div className="row mb-3">
                  <div className="col-12">
                    <Link
                      to="/led-para-comercio"
                      className="client-banner d-block"
                      style={{ height: '200px' }}
                    >
                      <img src="../img/clientes/banner_comercio.jpg" alt="Comércio - Atacadista de luminárias LED para estabelecimentos comerciais" style={{ height: '200px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Comércio</div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Segunda Linha - Revendedor 50% + Licitante 50% */}
                <div className="row mb-3">
                  <div className="col-6 pe-2">
                    <Link
                      to="/led-para-revendedor"
                      className="client-banner d-block"
                      style={{ height: '200px' }}
                    >
                      <img src="../img/clientes/banner_revendedor.jpg" alt="Revendedor - Distribuidora de LED para parceiros e revendedores autorizados" style={{ height: '200px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Revendedor</div>
                      </div>
                    </Link>
                  </div>
                  <div className="col-6 ps-2">
                    <Link
                      to="/led-para-licitante"
                      className="client-banner d-block"
                      style={{ height: '200px' }}
                    >
                      <img src="../img/clientes/banner_licitante.jpg" alt="Licitante - Distribuidora de LED para órgãos públicos e licitações governamentais" style={{ height: '200px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Licitante</div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Terceira Linha - Loja de Iluminação 50% + Empresa 50% */}
                <div className="row">
                  <div className="col-6 pe-2">
                    <Link
                      to="/led-para-loja-de-iluminacao"
                      className="client-banner d-block"
                      style={{ height: '200px' }}
                    >
                      <img src="../img/clientes/banner_loja.jpg" alt="Lojas de Iluminação - Atacadista de luminárias LED para lojas especializadas" style={{ height: '200px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Lojas de<br />Iluminação</div>
                      </div>
                    </Link>
                  </div>
                  <div className="col-6 ps-2">
                    <Link
                      to="/led-para-empresa"
                      className="client-banner d-block"
                      style={{ height: '200px' }}
                    >
                      <img src="../img/clientes/banner_empresa.jpg" alt="Empresa - Distribuidora de LED para soluções corporativas e empresariais" style={{ height: '200px', objectFit: 'cover' }} />
                      <div className="overlay">
                        <div className="client-name">Empresa</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection>
            <div className="text-center mt-4">
              <p><strong>e mais...</strong></p>
              <div className="text-links">
                <Link to="/led-para-condominio">CONDOMÍNIO</Link>
                <Link to="/led-para-construtora">CONSTRUTORA</Link>
                <Link to="/led-para-loja-de-materiais-eletricos">LOJA DE MATERIAIS ELÉTRICOS</Link><br />
                <Link to="/led-para-decorador-de-ambiente">DECORADOR DE AMBIENTE</Link>
                <Link to="/led-para-arquiteto">ARQUITETO</Link>
                <Link to="/led-para-engenheiro">ENGENHEIRO</Link>
                <Link to="/led-para-mestre-de-obras">MESTRE DE OBRAS</Link>
                <Link to="/led-para-igreja">IGREJA</Link><br />
                <Link to="/led-para-posto-de-combustivel">POSTO DE COMBUSTÍVEL</Link>
                <a href={generateWhatsAppLink('outro', 'outro')} target="_blank" rel="noopener noreferrer">OUTRO</a>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Estatísticas */}
      <section className="section stats-section">
        <div className="container">
          <FadeInSection>
            <div className="section-title">
              <h1>Nossos Números</h1>
              <h4>O que fizemos ao longo dos últimos anos</h4>
            </div>
          </FadeInSection>

          <div className="row">
            <div className="col-6 col-sm-6 col-md-3">
              <FadeInSection>
                <div className="stat-item">
                  <div className="stat-number">
                    <span style={{ fontWeight: '900', color: 'black' }}><AnimatedNumber end={313} />K</span>
                  </div>
                  <h2 style={{ fontWeight: '900', color: 'var(--primary-green)', margin: '0' }}>Vendas</h2>
                  <span style={{ fontWeight: '400', color: 'black' }}>Realizadas</span>
                </div>
              </FadeInSection>
            </div>
            <div className="col-6 col-sm-6 col-md-3">
              <FadeInSection>
                <div className="stat-item">
                  <div className="stat-number">
                    <span style={{ fontWeight: '900', color: 'black' }}><AnimatedNumber end={289} />K</span>
                  </div>
                  <h2 style={{ fontWeight: '900', color: 'var(--primary-green)', margin: '0' }}>Clientes</h2>
                  <span style={{ fontWeight: '400', color: 'black' }}>Atendidos</span>
                </div>
              </FadeInSection>
            </div>
            <div className="col-6 col-sm-6 col-md-3">
              <FadeInSection>
                <div className="stat-item">
                  <div className="stat-number">
                    <span style={{ fontWeight: '900', color: 'black' }}>7,8M</span>
                  </div>
                  <h2 style={{ fontWeight: '900', color: 'var(--primary-green)', margin: '0' }}>Produtos</h2>
                  <span style={{ fontWeight: '400', color: 'black' }}>Vendidos</span>
                </div>
              </FadeInSection>
            </div>
            <div className="col-6 col-sm-6 col-md-3">
              <FadeInSection>
                <div className="stat-item">
                  <div className="stat-number">
                    <span style={{ fontWeight: '900', color: 'black' }}><AnimatedNumber end={1859} /></span>
                  </div>
                  <h2 style={{ fontWeight: '900', color: 'var(--primary-green)', margin: '0' }}>Projetos</h2>
                  <span style={{ fontWeight: '400', color: 'black' }}>Executados</span>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* Produtos */}
      <section id="produtos" className="section">
        <div className="container">
          <FadeInSection>
            <div className="section-title">
              <div className="badge-green">Produtos</div>
              <h1>Estoque completo em LED<br />e decoração de ambientes</h1>
              <h4>Acesse todo o nosso catálogo em nossa loja</h4>
            </div>
          </FadeInSection>

          <div className="products-grid">
            <div className="row">
              {/* Primeira Coluna */}
              <div className="col-lg-6">
                {/* Primeira Linha - Refletores 100% */}
                <div className="row mb-3 product-line-1">
                  <div className="col-12">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/refletores-holofote-led" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_refletores.jpg" alt="Refletores LED - Distribuidora de LED profissional para iluminação externa e industrial" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Refletores</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>

                {/* Segunda Linha - Linha Solar 100% */}
                <div className="row mb-3 product-line-2">
                  <div className="col-12">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/luminaria-publica-solar-eletrica" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_linha_solar.jpg" alt="Linha Solar" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Linha Solar</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>

                {/* Terceira Linha - Decoração 50% + Fitas 50% */}
                <div className="row product-line-3">
                  <div className="col-6">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/lustres-e-pendentes" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_decoracao.jpg" alt="Decoração" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Decoração</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                  <div className="col-6">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/fita-led-mangueira-led" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_fitas.jpg" alt="Fitas LED - Distribuidora de LED decorativas e funcionais para diversos ambientes" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Fitas LED</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>
              </div>

              {/* Segunda Coluna */}
              <div className="col-lg-6">
                {/* Primeira Linha - Spots 50% + Lâmpadas 50% */}
                <div className="row mb-3 product-line-1">
                  <div className="col-6">
                    <FadeInSection>
                      <a href="http://sustentaled.com.br/spots-led-lampada-dicroica" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_spots.jpg" alt="Spots LED - Atacadista de luminárias LED para iluminação direcionada e decorativa" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Spots</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                  <div className="col-6">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/lampadas-led" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_lampadas.jpg" alt="Lâmpadas LED - Importadora de lâmpadas LED de alta qualidade e eficiência energética" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Lâmpadas LED</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>

                {/* Segunda Linha - Luminárias 100% */}
                <div className="row mb-3 product-line-2">
                  <div className="col-12">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/luminarias-plafon-led" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_luminarias.jpg" alt="Luminárias LED - Atacadista de luminárias LED para residencial, comercial e industrial" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Luminárias</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>

                {/* Terceira Linha - Públicas 100% */}
                <div className="row product-line-3">
                  <div className="col-12">
                    <FadeInSection>
                      <a href="https://www.sustentaled.com.br/luminaria-publica-solar-eletrica" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="product-card client-banner">
                          <img src="../img/produtos/b_publicas.jpg" alt="Iluminação Pública LED - Distribuidora de LED para soluções urbanas e municipais" className="img-fluid" />
                          <div className="overlay">
                            <div className="client-name">Iluminação Pública</div>
                            <div className="hover-text">ver produtos na loja</div>
                          </div>
                        </div>
                      </a>
                    </FadeInSection>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Cases */}
      <section id="cases" className="py-5">
        <div className="container">
          <FadeInSection>
            <div className="fade-in visible ">
              <div className="section-title">
                <span className="badge-green">Cases</span>
                <h1>Projetos executados com <br></br>produtos LED da Sustenta</h1>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection>
            <div className="row align-items-center mb-5">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <img src="/img/banner_castelao.jpg" alt="Estádio Castelão - Projeto de iluminação LED da Sustenta LED, distribuidora líder em soluções esportivas" className="img-fluid rounded" />
              </div>
              <div className="col-lg-6 l2">
                <h2 className="">Estádio Castelão</h2>
                <h3 className="h3 sub-title">Estádio Governador João Castelo</h3>
                <p className="text-justify">
                  Castelão é um estádio multiuso situado em São Luís, Maranhão, Brasil. Localizado no Complexo Esportivo Canhoteiro, no bairro Outeiro da Cruz, em São Luís, em uma área que abriga também o ginásio de esportes Castelinho, o maior ginásio do estado do Maranhão, uma ampla pista de atletismo, parque aquático, ginásio de artes marciais, pista de skate e pista de kart. Em 2024 a Sustenta LED participou da reforma do estádio com o fornecimento de sua iluminação.
                </p>
              </div>
            </div>
          </FadeInSection>

          {/* Seção Alguns Clientes */}
          <FadeInSection>
            <div className="section-title">
              <h1 className="">Alguns clientes</h1>
            </div>

            <div className="clients-carousel-container">
              <button className="carousel-arrow carousel-arrow-left" onClick={() => slideCarousel('prev')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="carousel-arrow carousel-arrow-right" onClick={() => slideCarousel('next')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="clients-carousel">
                <div className="clients-track" id="clients-track">

                  <div className="client-slide">
                    <img src="/img/marcas/marca_globo.jpg" alt="Globo - Cliente da Sustenta LED, distribuidora de LED para grandes empresas" className="client-logo" />
                  </div>
                  <div className="client-slide">
                    <img src="/img/marcas/marca_macdonalds.jpg" alt="McDonald's - Cliente da Sustenta LED, atacadista de luminárias LED para redes comerciais" className="client-logo" />
                  </div>
                  <div className="client-slide">
                    <img src="/img/marcas/marca_sbt.jpg" alt="SBT - Cliente da Sustenta LED, importadora de lâmpadas LED para mídia e entretenimento" className="client-logo" />
                  </div>
                  <div className="client-slide">
                    <img src="/img/marcas/marca_toyota.jpg" alt="Toyota - Cliente da Sustenta LED, distribuidora de LED para setor automotivo" className="client-logo" />
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Seção FAQ */}
      <section id="faq" className="py-5" style={{ backgroundColor: '#FBFBFC' }}>
        <div className="container">
          <FadeInSection>
            <div className="section-title text-center">
              <div className="badge-green">FAQ</div>
              <h1 style={{ fontWeight: '900' }}>Perguntas Frequentes</h1>
              <p className="text-muted">Tire suas dúvidas sobre nossos produtos e como podemos ajudar com o seu projeto</p>
            </div>
          </FadeInSection>

          <FadeInSection>
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="faq-container">
                  {[
                    {
                      question: "Quais são os produtos que Sustenta LED vende?",
                      answer: "A Sustenta LED oferece uma linha completa de produtos LED incluindo lâmpadas, luminárias, refletores, spots, fitas LED, linha solar, iluminação pública e produtos para decoração de ambientes. Trabalhamos com as melhores marcas do mercado para garantir qualidade e durabilidade."
                    },
                    {
                      question: "Em quanto tempo meu orçamento será enviado?",
                      answer: "Nossos orçamentos são elaborados e enviados em até 1 hora útil após o recebimento da sua solicitação. Para projetos mais complexos, o prazo pode ser de até 6 horas. Você receberá uma proposta detalhada com especificações técnicas e valores."
                    },
                    {
                      question: "Tenho um projeto de iluminação, como faço para encaminhar para cotação?",
                      answer: "Você pode nos enviar seu projeto através do WhatsApp, e-mail ou preenchendo nosso formulário online. Inclua plantas, especificações técnicas, metragem e qualquer detalhe relevante. Nossa equipe técnica analisará e elaborará uma proposta personalizada para seu projeto."
                    },
                    {
                      question: "Qual a diferença entre branco frio e branco quente?",
                      answer: "O branco quente (2700K-3000K) tem tonalidade amarelada, ideal para ambientes residenciais e aconchegantes. O branco frio (5000K-6500K) tem tonalidade azulada ou branca, perfeito para ambientes comerciais, escritórios e áreas que precisam de maior concentração. Também temos o branco neutro (4000K) que é um meio termo entre os dois."
                    },
                    {
                      question: "Tem valor para pedido mínimo? A Sustenta LED vende para pessoa física e pessoa jurídica?",
                      answer: "Sim, atendemos tanto pessoa física quanto jurídica! O valor mínimo para pedidos é de R$ 299,90, trabalhamos desde pequenas quantidades até grandes projetos. Oferecemos condições especiais para revendedores, construtoras e projetos de grande volume."
                    }
                  ].map((faq, index) => (
                    <FAQItem
                      key={index}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFAQIndex === index}
                      onToggle={() => setOpenFAQIndex(openFAQIndex === index ? null : index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Seção Contato */}
      <section id="contact" className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <FadeInSection>
            <div className="section-title text-center mb-5">
              <div className="badge-green">Contato</div>
              <h1 style={{ fontWeight: '900' }}>Fale Conosco</h1>
              <p className="text-muted">Entre em contato para solicitar um orçamento ou tirar dúvidas sobre nossos produtos / serviços</p>
            </div>
          </FadeInSection>

          <div className="row">
            {/* Informações de Contato */}
            <div className="col-lg-4 mb-4">
              <FadeInSection>
                <div className="contact-info h-100 d-flex flex-column">
                  <div className="contact-item mb-4 p-4 flex-fill">
                    <div className="d-flex align-items-center mb-3">
                      <div className="contact-icon me-3" style={{ width: '50px', height: '50px', backgroundColor: 'var(--primary-green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="bi bi-telephone" style={{ color: 'white', fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <h5 className="mb-1" style={{ color: '#333', fontWeight: '600' }}>Telefone</h5>
                        <p className="mb-0 text-muted">Vamos conversar?</p>
                      </div>
                    </div>
                    <a href="tel:+551156787018" className="text-decoration-none" style={{ color: 'var(--primary-green)', fontWeight: '400', display: 'block', lineHeight: '1.8', marginBottom: '10px' }}>
                      (11) 5678-7018
                    </a>
                    <a href={generateWhatsAppLink('outro', 'contato-telefone')} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: 'var(--primary-green)', fontWeight: '400', display: 'block', lineHeight: '1.8' }}>
                      (11) 94895-1961
                    </a>
                  </div>

                  <div className="contact-item mb-4 p-4 flex-fill">
                    <div className="d-flex align-items-center mb-3">
                      <div className="contact-icon me-3" style={{ width: '50px', height: '50px', backgroundColor: 'var(--primary-green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="bi bi-envelope" style={{ color: 'white', fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <h5 className="mb-1" style={{ color: '#333', fontWeight: '600' }}>E-mail</h5>
                        <p className="mb-0 text-muted">Envie sua mensagem</p>
                      </div>
                    </div>
                    <a href="mailto:comercial@sustentaled.com.br" className="text-decoration-none" style={{ color: 'var(--primary-green)', fontWeight: '400' }}>
                      comercial@sustentaled.com.br
                    </a>
                  </div>

                  <div className="contact-item mb-4 p-4 flex-fill" style={{ display: 'none' }}>
                    <div className="d-flex align-items-center mb-3">
                      <div className="contact-icon me-3" style={{ width: '50px', height: '50px', backgroundColor: 'var(--primary-green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="bi bi-geo-alt" style={{ color: 'white', fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <h5 className="mb-1" style={{ color: '#333', fontWeight: '600' }}>Endereço</h5>
                        <p className="mb-0 text-muted">Venha nos visitar</p>
                      </div>
                    </div>
                    <p className="mb-0" style={{ color: '#666', fontWeight: '500', lineHeight: '1.5' }}>
                      Avenida Interlagos, 2290 - Caixa Postal 77251<br />
                      Jardim Marajoara, São Paulo, SP<br />
                      CEP: 04660-970
                    </p>
                  </div>
                </div>
              </FadeInSection>
            </div>

            {/* Formulário de Contato */}
            <div className="col-lg-8">
              <FadeInSection>
                <div className="contact-form p-4 h-100 d-flex flex-column">
                  <h3 className="mb-4" style={{ color: '#333', fontWeight: '600' }}>Envie sua Mensagem</h3>
                  <form>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Nome Completo *"
                          value={formData.nome}
                          onChange={(e) => handleFormChange('nome', e.target.value)}
                          style={{
                            border: `2px solid ${formErrors.nome ? '#dc3545' : '#e9ecef'}`,
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '16px',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => {
                            if (!formErrors.nome) {
                              e.target.style.borderColor = 'var(--primary-green)';
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.nome ? '#dc3545' : '#e9ecef';
                          }}
                          aria-invalid={formErrors.nome}
                        />
                        {formErrors.nome && (
                          <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '6px' }}>
                            Informe o nome completo.
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="E-mail *"
                          value={formData.email}
                          onChange={(e) => handleFormChange('email', e.target.value)}
                          style={{
                            border: `2px solid ${formErrors.email ? '#dc3545' : '#e9ecef'}`,
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '16px',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => {
                            if (!formErrors.email) {
                              e.target.style.borderColor = 'var(--primary-green)';
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.email ? '#dc3545' : '#e9ecef';
                          }}
                          aria-invalid={formErrors.email}
                        />
                        {formErrors.email && (
                          <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '6px' }}>
                            Informe um e-mail válido.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Telefone *"
                          value={formData.telefone}
                          onChange={(e) => handleFormChange('telefone', formatPhone(e.target.value))}
                          style={{
                            border: `2px solid ${formErrors.telefone ? '#dc3545' : '#e9ecef'}`,
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '16px',
                            transition: 'border-color 0.3s ease'
                          }}
                          onFocus={(e) => {
                            if (!formErrors.telefone) {
                              e.target.style.borderColor = 'var(--primary-green)';
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.telefone ? '#dc3545' : '#e9ecef';
                          }}
                          inputMode="tel"
                          aria-invalid={formErrors.telefone}
                        />
                        {formErrors.telefone && (
                          <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '6px' }}>
                            Informe um telefone válido com DDD.
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <Select
                          placeholder="Assunto"
                          options={[
                            { value: 'orcamento', label: 'Solicitar Orçamento' },
                            { value: 'projeto', label: 'Projeto de Iluminação' },
                            { value: 'produtos', label: 'Informações sobre Produtos' },
                            { value: 'outros', label: 'Outros' }
                          ]}
                          value={formData.assunto ? (
                            formData.assunto === 'orcamento' ? { value: 'orcamento', label: 'Solicitar Orçamento' } :
                              formData.assunto === 'projeto' ? { value: 'projeto', label: 'Projeto de Iluminação' } :
                                formData.assunto === 'produtos' ? { value: 'produtos', label: 'Informações sobre Produtos' } :
                                  formData.assunto === 'outros' ? { value: 'outros', label: 'Outros' } : null
                          ) : null}
                          onChange={(opt) => handleFormChange('assunto', opt ? opt.value : '')}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              borderColor: state.isFocused ? 'var(--primary-green)' : '#e9ecef',
                              boxShadow: 'none',
                              borderWidth: 2,
                              borderRadius: 8
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected ? 'var(--primary-green)' : (state.isFocused ? '#f2f7e8' : 'white'),
                              color: state.isSelected ? 'white' : '#333'
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: '#666'
                            })
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <textarea
                        className="form-control"
                        rows="5"
                        placeholder="Mensagem *"
                        value={formData.mensagem}
                        onChange={(e) => handleFormChange('mensagem', e.target.value)}
                        style={{
                          border: '2px solid #e9ecef',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          fontSize: '16px',
                          transition: 'border-color 0.3s ease',
                          resize: 'vertical'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      ></textarea>
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        type="submit"
                        className="btn btn-lg px-4"
                        style={{
                          backgroundColor: 'var(--primary-green)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#6fa01a';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'var(--primary-green)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        <i className="bi bi-send me-2"></i>
                        Enviar Mensagem
                      </button>
                      <a
                        href={isFormValid ? generateWhatsAppMessageLink(generateWhatsAppMessageFromForm(), 'contato-form') : '#'}
                        target={isFormValid ? "_blank" : undefined}
                        rel={isFormValid ? "noopener noreferrer" : undefined}
                        className="btn btn-outline-success btn-lg px-4"
                        onClick={handleWhatsAppClick}
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease',
                          opacity: isFormValid ? 1 : 0.7
                        }}
                        aria-disabled={!isFormValid}
                      >
                        <i className="bi bi-whatsapp me-2"></i>
                        Enviar pelo WhatsApp
                      </a>
                    </div>
                  </form>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Produtos */}
      {showModal && selectedProduct && (
        <div className={`product-modal ${showModal ? 'show' : ''}`} onClick={closeProductModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="fw-bold text-success">{selectedProduct.title}</h2>
              <button className="modal-close" onClick={closeProductModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="product-carousel mb-4">
                <img
                  src={`/img/produtos/${selectedProduct.images[currentCarouselSlide]}`}
                  alt={selectedProduct.title}
                  className="w-100 rounded"
                  style={{ height: '300px', objectFit: 'cover' }}
                />
              </div>

              <p className="mb-4">{selectedProduct.description}</p>

              <div className="mb-4">
                <h5 className="fw-bold mb-3">Tipos disponíveis:</h5>
                <div className="row">
                  {selectedProduct.types.map((type, index) => (
                    <div key={index} className="col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <span>{type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-buttons">
                <a
                  href={generateWhatsAppMessageLink(`Olá! Gostaria de cotar produtos da categoria: ${selectedProduct.title}`, 'modal-cotar')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-lg"
                >
                  <i className="fab fa-whatsapp me-2"></i>
                  Cotar Agora
                </a>
                <a
                  href={generateWhatsAppMessageLink(`Olá! Gostaria de acessar o catálogo de: ${selectedProduct.title}`, 'modal-catalogo')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-success btn-lg"
                >
                  <i className="fas fa-catalog me-2"></i>
                  Ver Catálogo
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner iOS: instrução para instalar como app */}
      {showInstallBanner && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid #eee',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
            padding: '12px 16px',
            zIndex: 10000,
          }}
        >
          <div className="container d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              <span style={{ color: '#000' }}>
                Para instalar no iPhone: toque em <b>Compartilhar</b> (ícone de quadrado com seta) e escolha <b>Adicionar à Tela de Início</b>.
              </span>
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowInstallBanner(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
      {/* REMOVIDO: footer inline duplicado */}
      {/* Botão Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top-btn"
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '20px',
            width: '35px',
            height: '35px',
            backgroundColor: 'var(--primary-green)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '14px',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
        >
          ↑
        </button>
      )}

      {/* Botão WhatsApp (temporariamente desativado) */}
      {floatingWhatsAppEnabled && (
        <a
          href={generateWhatsAppMessageLink('Olá! Gostaria de mais informações sobre os produtos da Sustenta LED.', 'floating')}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-btn"
          style={{
            position: 'fixed',
            right: '20px',
            bottom: '20px',
            width: '60px',
            height: '60px',
            backgroundColor: 'var(--primary-green)',
            color: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            fontSize: '28px',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(128, 198, 30, 0.4)',
            transition: 'all 0.3s ease',
            animation: 'pulse 1.5s infinite'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 20px rgba(128, 198, 30, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(128, 198, 30, 0.4)';
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67 .15-.197.297-.767.966-.94 1.164-.173.199-.347 .223-.644 .075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458 .13-.606 .134-.133 .298-.347 .446-.52 .149-.174 .198-.298 .298-.497 .099-.198 .05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52 .074-.792 .372-.272 .297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074 .149 .198 2.096 3.2 5.077 4.487 .709 .306 1.262 .489 1.694 .625 .712 .227 1.36 .195 1.871 .118 .571-.085 1.758-.719 2.006-1.413 .248-.694 .248-1.289 .173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741 .982 .998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c .001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335 .157 11.892c 0 2.096 .547 4.142 1.588 5.945L .057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h .005c 6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488" />
          </svg>
        </a>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={closePopup}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80%',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePopup}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '5px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
            <div style={{ marginTop: '10px' }}>
              {popupContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
